import { DateTime } from "luxon";
import {
  Box,
  Typography,
  MenuItem,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import { AccessTime, Speed, Schedule, Business } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useRequestCountdowns } from "../../../hooks/useRequestCountdowns";

const NotaryNotifications = ({ onNotificationClick, notificationLoading }) => {
  const currentUser = useSelector((state) => state.notarySession?.user);
  const realTimeRequests = useSelector(
    (state) => state.notarySession?.notaryRequests || [],
  );
  const userTimezone =
    currentUser?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "America/New_York";
  const { formatTimeRemaining, getTimeRemaining, isExpired } =
    useRequestCountdowns();

  const formatDateTime = (value, timezone) => {
    if (!value) return "N/A";

    const targetTimezone =
      timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "America/New_York";

    let parsedDate;

    // Support unix timestamps (seconds/milliseconds)
    if (typeof value === "number") {
      parsedDate =
        value > 9999999999
          ? DateTime.fromMillis(value, { zone: "utc" })
          : DateTime.fromSeconds(value, { zone: "utc" });
    } else {
      const rawValue = String(value).trim();

      // Numeric string timestamp
      if (/^\d+$/.test(rawValue)) {
        const numericValue = Number(rawValue);
        parsedDate =
          rawValue.length > 10 || numericValue > 9999999999
            ? DateTime.fromMillis(numericValue, { zone: "utc" })
            : DateTime.fromSeconds(numericValue, { zone: "utc" });
      } else {
        // Most request timestamps arrive in UTC ISO format
        parsedDate = DateTime.fromISO(rawValue, { zone: "utc" });

        // Fallback parse attempt without forcing zone
        if (!parsedDate.isValid) {
          parsedDate = DateTime.fromISO(rawValue);
        }
      }
    }

    // If value is already a formatted string, show it as-is
    if (!parsedDate?.isValid) return String(value);

    return parsedDate.setZone(targetTimezone).toFormat("MMM dd, hh:mm a");
  };

  const getRequestTypeInfo = (type) => {
    const isImmediate = type === "immediate";
    return {
      type: isImmediate ? "Expedited Request" : "Later Date Request",
      color: isImmediate ? "error" : "warning",
      icon: isImmediate ? <Speed /> : <Schedule />,
    };
  };

  if (!realTimeRequests || realTimeRequests.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          No live notarization requests
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: "400px",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      {realTimeRequests.map((request, index) => {
        const requestTypeInfo = getRequestTypeInfo(
          request.instant ? "immediate" : "later",
        );
        const timeRemainingSeconds = getTimeRemaining(request.id);
        const timeRemaining = formatTimeRemaining(timeRemainingSeconds);
        const requestExpired = isExpired(request.id);
        const isLoading = notificationLoading === request.id;

        return (
          <MenuItem
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              p: 2,
              minHeight: "auto",
              borderBottom:
                index < realTimeRequests.length - 1
                  ? "1px solid #e0e0e0"
                  : "none",
              backgroundColor: requestExpired ? "#f8f9fa" : "inherit",
              cursor: isLoading ? "default" : "pointer",
              "&:hover": {
                backgroundColor: isLoading ? "inherit" : "#f0f7ff",
              },
              "&:last-child": {
                borderBottom: "none",
              },
            }}
            disableRipple={true}
            onClick={() => {
              if (!isLoading) {
                onNotificationClick("realtime");
              }
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              >
                <CircularProgress size={25} />
              </Box>
            )}

            {/* Header with request type and time remaining */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {requestTypeInfo.icon}
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {requestTypeInfo.type}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime
                  fontSize="small"
                  color={requestExpired ? "error" : "warning"}
                />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={requestExpired ? "error" : "warning"}
                >
                  {timeRemaining}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            {/* Request details */}
            <Stack spacing={1.5}>
              {/* Company/Requester */}
              <Box display="flex" alignItems="center" gap={1}>
                <Business fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  <strong>Company:</strong> {request.requestor || "N/A"}
                </Typography>
              </Box>

              {/* Request date */}
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  <strong>Requested:</strong>{" "}
                  {formatDateTime(
                    request.created_at || request.date,
                    userTimezone,
                  )}
                </Typography>
              </Box>

              {/* Action hint */}
              <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #f0f0f0" }}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    fontStyle: "italic",
                    fontWeight: "medium",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  👆 Click to view details and manage request
                </Typography>
              </Box>
            </Stack>
          </MenuItem>
        );
      })}
    </Box>
  );
};

export default NotaryNotifications;
